import { create } from 'zustand';
import { defaultUserSettings, loadUserSettings, saveUserSettings } from './settingsStorage';
import { useStationFilter } from '../SearchStation/store';
import { getRestoredHolderFilterAreas, normalizeHolderFilterAreas } from './holderFilter.helper';
import References from '../../helpers/Reference';
import AuthService from '../../helpers/auth';

export const useAuth = create(() => ({
    isProcessing: true,
    access_token: '',
    refresh_token: '',
    permission: {},
    isAuth: false,
    isAuthorized: false,
    isAnonymus: false,
    authError: '',
    userSettings: loadUserSettings(),
    settingsLoaded: false,
    settingsHydrated: false
  }));

export const hydrateUserSettings = (userId) => {
  const settings = loadUserSettings(userId);
  useAuth.setState({
    userSettings: settings,
    settingsLoaded: true,
    settingsHydrated: true
  });

  const restoredAreas = getRestoredHolderFilterAreas(settings);
  if (restoredAreas.length > 0) {
    useStationFilter.setState({ areas: restoredAreas });
  }

  return settings;
};

// Fetch settings stored on the server and merge appropriately with local defaults/localStorage
export const initializeUserSettings = async (userId) => {
  let serverSettings = {};

  try {
    const res = await AuthService.fetch('/api/user_settings/us1/');
    if (res && res.ok) {
      const json = await res.json();
      // server may return either { settings: {...} } or { us1: { version, settings } }
      if (json) {
        if (json.settings) serverSettings = json.settings;
        else if (json.us1 && json.us1.settings) serverSettings = json.us1.settings;
        else serverSettings = {};
      }
    }
  } catch (e) {
    // ignore — fallback to local/defaults
    console.warn('Не удалось получить настройки с сервера', e);
  }

  const localSettings = loadUserSettings(userId);

  // Build final settings: for keys that are serverSave — prefer server value (if present) otherwise default;
  // for others — prefer localSettings.
  const catalog = References.settingsCatalog || {};
  const finalSettings = { ...defaultUserSettings };

  Object.keys(finalSettings).forEach(k => {
    const meta = catalog[k];
    if (meta && meta.serverSave) {
      if (serverSettings.hasOwnProperty(k)) finalSettings[k] = serverSettings[k];
      else finalSettings[k] = meta.default ?? defaultUserSettings[k];
    } else {
      finalSettings[k] = localSettings.hasOwnProperty(k) ? localSettings[k] : defaultUserSettings[k];
    }
  });

  useAuth.setState({
    userSettings: finalSettings,
    settingsLoaded: true,
    settingsHydrated: true
  });

  const restoredAreas = getRestoredHolderFilterAreas(finalSettings);
  if (restoredAreas.length > 0) {
    useStationFilter.setState({ areas: restoredAreas });
  }

  // persist only local / non-server settings to localStorage; server-backed values are initialized by GET and stay on the server
  saveUserSettings(finalSettings, userId);

  return finalSettings;
};

export const updateUserSettings = (patch) => {
  const currentSettings = { ...defaultUserSettings, ...useAuth.getState().userSettings };
  const nextSettings = { ...currentSettings, ...patch };

  useAuth.setState({ userSettings: nextSettings });

  // determine current logged-in user id if any
  const permission = useAuth.getState().permission || {};
  const userId = permission?.user_id || permission?.user || permission?.id || permission?.sub || permission?.username || null;

  saveUserSettings(nextSettings, userId);

  return nextSettings;
};

export const setUserSetting = (key, value) => {
  const next = updateUserSettings({ [key]: value });

  // if this setting is configured to be saved on server — collect all serverSave settings that differ from defaults and POST
  try {
    const catalog = References.settingsCatalog || {};
    const meta = catalog[key];
    if (meta && meta.serverSave) {
      const payload = {};
      Object.keys(catalog).forEach(k => {
        const cm = catalog[k];
        if (!cm || !cm.serverSave) return;
        const currentVal = next[k] !== undefined ? next[k] : (cm.default ?? defaultUserSettings[k]);
        const defaultVal = cm.default ?? defaultUserSettings[k];
        if (String(currentVal) !== String(defaultVal)) {
          payload[k] = currentVal;
        }
      });

      // send to server (fire-and-forget)
      AuthService.post('/api/user_settings/us1/', { version: '1.0', settings: payload })
        .then(res => {
          if (!res.ok) console.warn('Ошибка сохранения настроек на сервере', res.status);
        })
        .catch(e => console.warn('Ошибка при POST user_settings', e));
    }
  } catch (e) {
    console.warn('Ошибка при синхронизации настроек', e);
  }

  return next;
};

export const syncHolderFilterToSettings = (areas) => {
  const settings = useAuth.getState().userSettings;
  const normalizedAreas = normalizeHolderFilterAreas(areas);

  if (Number(settings?.setting_holder_filter) === 1) {
    updateUserSettings({
      setting_holder_filter_areas: normalizedAreas
    });
  }
};

export const clearSavedHolderFilter = () => {
  updateUserSettings({
    setting_holder_filter: 0,
    setting_holder_filter_areas: []
  });
};